define('extensions/i18n/knockout.i18n',[
    'lib/lodash4',
    'lib/knockout',
    'lib/hogan',
    'lib/globalize',
    'moment',
    'extensions/i18n/defaultTags',
    'lib/libphonenumber',
    'services/loggingService',
    'util/momentUtils',

    // No references
    'extensions/i18n/knockout.lString',
    'extensions/i18n/knockout.lAttr',
    'extensions/i18n/knockout.lDate',
    'extensions/i18n/knockout.lDuration',
    'extensions/i18n/knockout.lTemplate',
    'extensions/i18n/knockout.lNumber'
], function (
    _,
    ko,
    hogan,
    globalize,
    moment,
    defaultTags,
    libphonenumber,
    loggingService,
    momentUtils
    ) {
    'use strict';
    var
        appLocale,            // Stores the locale of the application that has been explicitly set
        compiledTags = {},    // Compiled tag templates
        compiledStrings = {}; // Container for compiled string templates

    /**
     * Compiles string templates and merges them into an existing collection of
     * compiled templates.
     *
     * @param {Object} uncompiledObject Key value pairs of the form stringId -> uncompiledTemplate.
     * @param {Object} compiledObject Key value pairs of the form stringId -> compiledTemplate. Entries
     *        from uncompiledObject will be merged with this collection.
     */
    var compileAndMerge = function (uncompiledObject, compiledObject) {
        for (var key in uncompiledObject) {
            if (uncompiledObject.hasOwnProperty(key)) {
                compiledObject[key] = hogan.compile(uncompiledObject[key]);
            }
        }
    };

    /**
     * Gets the currently set locale.
     * @return {String} the current locale in use.
     */
    var getCurrentLocale = function () {
        return appLocale;
    };


    var phoneformatter = libphonenumber;

    /**
     * Extract a number when the SIP address is like
     * sip:+18257567163@127.0.0.1
     */
    var extractSipNumber = function (sipAddress) {
        var num = sipAddress.substr(4);
        var stripAfter = num.indexOf('@');
        num = num.substr(0, stripAfter);
        return num;
    };

    /**
     * Extract a SIP address where the format is like:
     * sip:jerry@inin.com
     * sip:jerrysSIP@127.0.0.1;someparam=user
     */
    var extractSipAddress = function (sipAddress) {
        var address = sipAddress.substr(4);
        var stripAfter = address.indexOf(';');
        if (stripAfter > 0) {
            address = address.substr(0, stripAfter);
        }
        return address;
    };

    /**
     * format an international number
     * @param  {String} num - a string representation of a phone number
     * @return {String}     - The formatted string
     */
    var formatInternationalNumber = function (num) {

        var phoneUtil = phoneformatter.PhoneNumberUtil.getInstance();

        var parsedNumber = phoneUtil.parse(num);
        var formattedNumber = phoneUtil.format(parsedNumber, 1); // 1 is for International
        return formattedNumber;
    };

    /**
     * Core internationalization object. Provides methods for
     * choosing locale and performing basic localization tasks.
     * @exports extensions/i18n/knockout-i18n
     */
    ko.i18n = {

        /**
         * format a phone address
         * @param  {String} number - a string representation of a phone address
         * @return {String} formattedAddress - a formatted String.
         */
        formatPhoneAddress: function (number) {
            if (!number) {
                return;
            }

            number = number + ''; //coerce to string

            var formattedPhoneAddress;

            try {
                if (number.indexOf('sip:') === 0) {
                    if (number.indexOf('+') === 4) {
                        formattedPhoneAddress = formatInternationalNumber(extractSipNumber(number));
                    } else {
                        return extractSipAddress(number);
                    }
                } else if (number.indexOf('tel:') === 0) {
                    formattedPhoneAddress = formatInternationalNumber(number.substring(4));
                } else if (number.indexOf('+') === 0) {
                    formattedPhoneAddress = formatInternationalNumber(number);
                }
            }
            catch(e) {
                loggingService.debug("Failed to format phone number: " + number + ". " + e);
            }

            if (!formattedPhoneAddress) {
                return number;
            }

            return formattedPhoneAddress;
        },

        /**
         * Sets the locale the application should use for localization. Default
         * locale on initialization is "en-US".
         *
         * @param {String} locale The locale to use, format should be xx-XX.
         */
        setLocale: function (locale) {
            appLocale = locale || "en-US"; // if locale is not set or is an empty string, default to "en-US"

            moment.locale(momentUtils.normalizeMomentLocale(appLocale));
            globalize.culture(appLocale);
        },

        /**
         * Get the currently set locale.
         */
        getLocale: function () {
            return appLocale;
        },

        /**
         * Whether or not the locale uses 12-hour format for displaying time.
         */
        isLocale12Hour: function () {
            return moment.localeData(appLocale).longDateFormat('LT').indexOf('A') > -1;
        },

        /**
         * Registers string templates for use within a specific locale.
         *
         * @param {Object} strings Key value pairs of stringId -> string template
         * @param {String} [locale] The locale to register the strings under,
         *        defaults to the currently set locale. Format should be xx-XX.
         */
        registerStrings: function (strings, locale) {
            var
                registerLocale = locale || getCurrentLocale(),
                compiledLocaleStrings = compiledStrings[registerLocale];

            if (!compiledLocaleStrings) {
                compiledLocaleStrings = compiledStrings[registerLocale] = {};
            }

            compileAndMerge(strings, compiledLocaleStrings);
        },

        /**
         * Registers html tags for use within string templates.
         *
         * @param {Object} tags Key value pairs of form tagId -> tagString
         */
        registerTags: function (tags) {
            compileAndMerge(tags, compiledTags);
        },

        /**
         * Renders a named string template.
         *
         * @param {Object}  The object that contains named properties name (required),
         *                  data (optional), and locale (optional).
         *
         * -- or --
         *
         * @param {String}  The name of the string to be rendered.
         * @param {Object}  Data used for token replacement within the string.
         * @param {String}  Locale to render the string in. Defaults to the current set locale.
         * @param {Boolean} Whether you want to log a message if the requested string is not found, default: true.
         *
         * @return {String} The rendered string, or an empty string if no localized string is found corresponding
         *         to the provided name.
         */
        renderString: function () {

            var args = arguments;
            var rawName, rawData, rawLocale;
            var logNotFound = true;

            if(_.isPlainObject(ko.unwrap(args[0]))){

                var obj = ko.unwrap(args[0]);
                rawName   = ko.unwrap(obj.name);
                rawData   = ko.unwrap(obj.data);
                rawLocale = ko.unwrap(obj.locale);
                if (!_.isUndefined(ko.unwrap(obj.logNotFound))) {
                    logNotFound = ko.unwrap(obj.logNotFound);
                }

            } else {

                rawName   = ko.unwrap(args[0]);
                rawData   = ko.unwrap(args[1]);
                rawLocale = ko.unwrap(args[2]);
                if (!_.isUndefined(ko.unwrap(args[3]))) {
                    logNotFound = ko.unwrap(args[3]);
                }

                // data parameter is optional
                if (!rawLocale && typeof rawData === 'string') {
                    rawLocale = rawData;
                    rawData = undefined;
                }

            }

            var renderLocale = rawLocale || getCurrentLocale();
            var string = compiledStrings[renderLocale] ? compiledStrings[renderLocale][rawName] : null;

            // Fall through to English.
            if (!string) {
                string = compiledStrings['en-US'] ? compiledStrings['en-US'][rawName] : null;
            }

            if (!string) {
                if (logNotFound) {
                    loggingService.info(rawName + " is not localized.");
                }
                return '';
            } else {
                return string.render(rawData, compiledTags);
            }
        },

        /**
         * Wrapper method around Globalize.format. See documentation at:
         * {@link https://github.com/jquery/globalize#numbers}
         */
        format: function (value, format, locale) {
            var localeToUse = locale || getCurrentLocale();

            // If you pass a string or Date obejct we're going to try and
            // format it as a date with moment
            if (moment(value).isValid() && (typeof value === "string" || _.isDate(value))) {
                return this.formatDate(value, format, localeToUse);
            }

            return globalize.format(value, format, localeToUse);
        },

        /**
         * Date-formatting method using moment.js.
         * Valid date formats for moment may differ from globalize.
         * See documentation at:
         * {@link http://momentjs.com/docs/#/displaying/format/}
         */
        formatDate: function (value, format, locale) {
            if (format) {
                if (locale) {
                    return moment(value).locale(momentUtils.normalizeMomentLocale(locale)).format(format);
                }
                return moment(value).format(format);
            } else {
                return moment(value).format("L");
            }

        },

        /**
         * Format a duration of time into a localized format that is easily consumable by
         * end users. This method will take a duration and perform the necessary rollups
         * of time according to the following logic:
         *
         * - if the duration is < 1 second, display "minutes:seconds.milliseconds"
         * - if the duration is >= 1 second and < 60 minutes, display "minutes:seconds"
         * - if the duration is >= 1 hour and <= 24 hours, display "hours:minutes:seconds"
         * - if the duration is >= 1 day and <= 365 days, display "x days"
         * - if the duration is >= 1 year, display "x years"
         *
         * @param  {Integer} duration The length of time, in milliseconds that will be used
         *  to create a localized, human readable string.
         * @return {String} A localized string representing the length of the time duration.
         *  If the provided duration argument is null or undefined, null will be returned.
         */
        formatDuration: function (duration) {
            var self = this;

            //
            // Private Methods
            //

            var formatAsTime = function (duration, messageKey) {
                // Since the formatting is based on a Date object, we create a moment that is set
                // to 00:00:00 (24 hour clock) and add the duration of time (in milliseconds) to
                // this moment object. This will create a Date object that has the correct hours,
                // minutes, and seconds values that we can use to render a string for display.
                var date = moment().startOf("day").add(duration, "milliseconds").toDate();
                return self.formatDate(date, self.renderString(messageKey));
            };

            var formatAsString = function (durationAmt, singularString, pluralString) {
                var messageKey = pluralString;

                if (durationAmt === 1) {
                    messageKey = singularString;
                }

                return self.renderString(messageKey, {duration: durationAmt});
            };

            var formatAsDayString = function (durObj) {
                var days = Math.floor(durObj.asDays());
                return formatAsString(days, "timeDuration.day", "timeDuration.days");
            };

            var formatAsYearString = function (durObj) {
                var years = Math.floor(durObj.asYears());
                return formatAsString(years, "timeDuration.year", "timeDuration.years");
            };

            //
            // Main Logic
            //

            if (_.isNull(duration) || _.isUndefined(duration)) {
                return null;
            }

            var durObj = moment.duration(duration);

            if (durObj.asSeconds() < 1) {
                return formatAsTime(duration, "timeDuration.minutesSecondsMilliseconds");
            }

            if (durObj.asSeconds() >= 1 && durObj.asMinutes() < 60) {
                return formatAsTime(duration, "timeDuration.minutesSeconds");
            }

            if (durObj.asHours() >= 1 && durObj.asHours() < 24) {
                return formatAsTime(duration, "timeDuration.hoursMinutesSeconds");
            }

            if (durObj.asDays() >= 1 && durObj.asDays() < 365) {
                return formatAsDayString(durObj);
            }

            return formatAsYearString(durObj);
        },

        /**
         * Wrapper method around Globalize.parseInt. See documenation at:
         * {@link https://github.com/jquery/globalize#globalizeparseint-value-radix-culture-}
         */
        parseInt: function (value, radix, locale) {
            var localeToUse = locale || getCurrentLocale();
            return globalize.parseInt(value, radix, localeToUse);
        },

        /**
         * Wrapper method around Globalize.parseFloat. See documenation at:
         * {@link https://github.com/jquery/globalize#globalizeparsefloat-value-radix-culture-}
         */
        parseFloat: function (value, radix, locale) {
            var localeToUse = locale || getCurrentLocale();
            return globalize.parseFloat(value, radix, localeToUse);
        },

        /**
         * Wrapper method around Globalize.parseDate. See documenation at:
         * {@link https://github.com/jquery/globalize#globalizeparsedate-value-formats-culture-}
         */
        parseDate: function (value, formats, locale) {
            var localeToUse = locale || getCurrentLocale();
            return globalize.parseDate(value, formats, localeToUse);
        },

        /**
         * Determines if a localization key exists.
         * @param key The localization key to check for.
         */
        keyExists: function (name, locale) {
            var renderLocale = locale || getCurrentLocale();
            if (compiledStrings[renderLocale] && compiledStrings[renderLocale][name]) {
                return true;
            }
            return false;
        },

        /**
         * Gets short name for locales weekdays
         * @returns Array of text strings ex: "Mo", "Tu" etc...
         */
        getShortDaysOfWeek: function(locale) {
            var localeToUse = locale || getCurrentLocale();
            return globalize.culture(localeToUse).calendar.days.namesShort;
        },

        /**
         * Gets the abbreviated name of the locals months
         * @returns Array of text strings; ex: "Jan", "Feb", etc...
         */
        getShortMonths: function(locale) {
            var localeToUse = locale || getCurrentLocale();
            var momentLocaleToUse = momentUtils.normalizeMomentLocale(localeToUse);
            var momentLocale = moment().locale(momentLocaleToUse);
            
            // If moment has a locale file for this locale, use moment's data instead of globalize
            if (_.isObject(momentLocale) && _.lowerCase(momentLocale.locale()) === _.lowerCase(momentLocaleToUse) && momentLocale.localeData()._monthsShort) {
                return momentLocale.localeData().monthsShort();
            } else {
                return globalize.culture(localeToUse).calendar.months.namesAbbr;
            }
        },

        /**
         * Gets the first day of chosen locale
         * @returns Integer to be used as index to return starting day of the week for locale
         */
        getFirstDay: function(locale) {
            var localeToUse = locale || getCurrentLocale();
            return globalize.culture(localeToUse).calendar.firstDay;
        },

        /**
         * Returns the relative time from now
         * @param {String} datetime - A string representation of the date and time
         * @param {String} locale - The locale to use, format should be xx-XX
         */
        fromNow: function(datetime, locale) {
            var localeToUse = locale || getCurrentLocale();
            return moment(datetime).locale(localeToUse).fromNow();
        }
    };

    //compile built-in tags;
    ko.i18n.registerTags(defaultTags);

    return ko.i18n;
});

